import React, { useEffect, useState } from "react";
import { Form, Input, Select, Checkbox, message } from "antd";
import FormModal from "../../../components/FormModal";
import PasswordFields from "../../../components/PasswordFields";
import { extractMsg } from "../../../utils/errorUtils";

export function AddUserDialog({ open, onClose, onSave }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      await onSave(values);
      form.resetFields();
      onClose();
    } catch (error) {
      message.error(extractMsg(error, "Failed to add user"));
      console.error("Failed to add user:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      title="Add New User"
      open={open}
      onClose={onClose}
      onFinish={handleFinish}
      loading={loading}
      form={form}
    >
      <Form.Item
        name="email"
        label="Email"
        rules={[{ required: true, type: "email" }]}
      >
        <Input placeholder="Enter email address" />
      </Form.Item>
      <Form.Item
        name="username"
        label="Username"
        rules={[{ required: true, min: 6, max: 24 }]}
      >
        <Input placeholder="Enter username" />
      </Form.Item>
      <PasswordFields />
    </FormModal>
  );
}

export function EditUserDialog({ open, user, onClose, onSave }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && user)
      form.setFieldsValue({ username: user.username, email: user.email });
  }, [open, user, form]);

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      await onSave(user.id, values);
      onClose();
    } catch (error) {
      message.error(extractMsg(error, "Failed to edit user"));
      console.error("Failed to edit user:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      title="Edit User"
      open={open}
      onClose={onClose}
      onFinish={handleFinish}
      loading={loading}
      form={form}
    >
      <Form.Item
        name="username"
        label="Username"
        rules={[{ required: true, min: 6, max: 24 }]}
      >
        <Input placeholder="Enter username" />
      </Form.Item>
      <Form.Item
        name="email"
        label="Email"
        rules={[{ required: true, type: "email" }]}
      >
        <Input placeholder="Enter email address" />
      </Form.Item>
    </FormModal>
  );
}

export function EditRoleDialog({ open, user, onClose, onSave }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && user) {
      const userRole = user.roles?.find((r) => r.name === "ROLE_ADMIN")
        ? "ADMIN"
        : "USER";
      form.setFieldsValue({ role: userRole });
    }
  }, [open, user, form]);

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      await onSave(user.id, values.role);
      onClose();
    } catch (error) {
      message.error(extractMsg(error, "Failed to edit role"));
      console.error("Failed to edit role:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      title="Edit User Role"
      open={open}
      onClose={onClose}
      onFinish={handleFinish}
      loading={loading}
      form={form}
    >
      <Form.Item name="role" label="Role" rules={[{ required: true }]}>
        <Select placeholder="Select a role">
          <Select.Option value="USER">User</Select.Option>
          <Select.Option value="ADMIN">Admin</Select.Option>
        </Select>
      </Form.Item>
    </FormModal>
  );
}

export function ManageAccessDialog({ open, user, onClose, onSave }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && user) {
      form.setFieldsValue({
        predict: user.hasPredictAccess || false,
        eeda: user.hasEedaAccess || false,
        kbase: user.hasKbaseAccess || false,
      });
    }
  }, [open, user, form]);

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      await onSave(user.id, values);
      onClose();
    } catch (error) {
      message.error(extractMsg(error, "Failed to update access"));
      console.error("Failed to update access:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      title="Manage User Access"
      open={open}
      onClose={onClose}
      onFinish={handleFinish}
      loading={loading}
      form={form}
    >
      <Form.Item name="predict" valuePropName="checked">
        <Checkbox>
          <img
            src="/img/majesteye-icon-skeye-predict-square-white-background-no-label.png"
            style={{ width: "20px", height: "20px" }}
            alt="SkeyePredict"
          />{" "}
          <span className="dialog-checkbox-label">SkeyePredict Access</span>
        </Checkbox>
      </Form.Item>
      <Form.Item name="eeda" valuePropName="checked">
        <Checkbox>
          <img
            src="/img/eeda-icon.svg"
            style={{ width: "20px", height: "20px" }}
            alt="EEDA"
          />{" "}
          <span className="dialog-checkbox-label">EEDA Access</span>
        </Checkbox>
      </Form.Item>
      <Form.Item name="kbase" valuePropName="checked">
        <Checkbox>
          <img
            src="/img/k-base-icon.svg"
            style={{ width: "20px", height: "20px" }}
            alt="KBASE"
          />{" "}
          <span className="dialog-checkbox-label">KBASE Access</span>
        </Checkbox>
      </Form.Item>
    </FormModal>
  );
}
